import { Field, useFormikContext } from 'formik';
import { ClipboardEvent, useRef } from 'react';
import { Button, Form } from 'react-bootstrap';

import {
  FormAddressData,
  useEnhancedAddressSearchWithCompletion,
} from '@/resources/hooks/useEnhancedAddressSearch';
import { useWebContext } from '@/resources/hooks/useWebContext';
import { maskZipcode, scriptChecker } from '@/services/global';

import { ControlLabel } from '../../Form/ControlLabel';
import { InputGroup } from '../../Form/Inputgroup';
import { LabelWrapper } from '../../Form/LabelWrapper';
import { RowGroup } from '../../Form/RowGroup';
import { IAddressModalStep, IAddressValues } from './types';

export function AddAddressForm({
  setModalStep,
}: {
  setModalStep: (step: IAddressModalStep) => void;
}) {
  const { configs } = useWebContext();
  const { setFieldValue } = useFormikContext<IAddressValues>();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handlePasteZipcode = async (e: ClipboardEvent<HTMLInputElement>) => {
    const value = e.clipboardData?.getData('text/plain');
    const input = e.target as HTMLInputElement;
    const maskedValue = maskZipcode(value);

    const cleanValue = value?.replace(/\D/g, '') || '';
    if (cleanValue.length !== 8 || !/^\d{8}$/.test(cleanValue)) {
      input.value = 'CEP informado é inválido';
      return;
    }

    input.value = maskedValue;

    let data = await useEnhancedAddressSearchWithCompletion(value);

    if (!data) return;

    if (data === 'CEP informado é inválido') {
      input.value = data;
      return;
    }

    data = data as FormAddressData;
    setFieldValue('street', data.street || '');
    setFieldValue('city', data.city || '');
    setFieldValue('state', data.state || '');
    setFieldValue('neighborhood', data.neighborhood || '');
    setFieldValue('zipcode', data.zipcode || '');
  };

  const handleChangeZipcode = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = maskZipcode(e.target.value);
    setFieldValue('zipcode', maskZipcode(e.target.value));

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (e.target.value.length === 9) {
      const { value } = e.target;
      const maskedValue = maskZipcode(value);

      timeoutRef.current = setTimeout(async () => {
        let data = await useEnhancedAddressSearchWithCompletion(value);

        if (!data) return;

        const cleanValue = value?.replace(/\D/g, '') || '';
        if (cleanValue.length !== 8 || !/^\d{8}$/.test(cleanValue)) {
          e.target.value = 'CEP informado é inválido';
          return;
        }

        e.target.value = maskedValue;

        if (!data) return;

        if (data === 'CEP informado é inválido') {
          e.target.value = data;
          return;
        }

        data = data as FormAddressData;
        setFieldValue('street', data.street || '');
        setFieldValue('city', data.city || '');
        setFieldValue('state', data.state || '');
        setFieldValue('neighborhood', data.neighborhood || '');
        setFieldValue('zipcode', data.zipcode || '');
      }, 500);
    }
  };

  const validateNeighborhood = (value: string) => {
    if (!value) return true;
    const cleanValue = value.trim().toLowerCase();

    if (cleanValue.length <= 1) {
      return true;
    }

    const firstChar = cleanValue[0];
    const hasIdenticalChars = cleanValue.split('').every((char) => char === firstChar);

    return !hasIdenticalChars;
  };

  const handleNeighborhoodKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const currentValue = (e.target as HTMLInputElement).value;
    const newValue = currentValue + e.key;

    if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
      return;
    }

    if (!validateNeighborhood(newValue)) {
      e.preventDefault();
    }
  };

  return (
    <div className="p-2">
      <RowGroup>
        <InputGroup className="w-100">
          <div className="d-flex justify-content-between align-items-center">
            <ControlLabel required={true}>CEP:</ControlLabel>
            {configs.allow_google_address_search && (
              <Form.Label className="mb-0">
                <Button
                  variant="link"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    setModalStep('addAddress');
                  }}
                >
                  <span style={{ fontSize: '0.8rem' }}>Esqueci meu CEP</span>
                </Button>
              </Form.Label>
            )}
          </div>
          <Field
            type="text"
            name="zipcode"
            placeholder="00000-000"
            data-type="address"
            required
            className="form-control data-input mb-lg-0 mb-2"
            onChange={handleChangeZipcode}
            onPaste={handlePasteZipcode}
          />
        </InputGroup>
      </RowGroup>
      <RowGroup>
        <InputGroup style={{ width: '65%' }}>
          <LabelWrapper>
            <ControlLabel required>Endereço:</ControlLabel>
          </LabelWrapper>

          <Field
            type="text"
            name="street"
            required
            data-type="address"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (scriptChecker(e?.target.value)) {
                e.target.value = '';
                setFieldValue('street', '');
                return;
              }
              setFieldValue('street', e.target.value);
            }}
            placeholder="Avenida dos Funcionários"
            className="form-control data-input mb-lg-0 mb-2"
          />
        </InputGroup>
        <InputGroup style={{ width: '35%' }}>
          <LabelWrapper>
            <ControlLabel required>Número:</ControlLabel>
          </LabelWrapper>

          <Field
            type="number"
            name="number"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (scriptChecker(e?.target.value)) {
                e.target.value = '';
                setFieldValue('number', '');
                return;
              }
              setFieldValue('number', e.target.value);
            }}
            required
            data-type="address"
            placeholder="102 ou s/n..."
            className="form-control data-input mb-lg-0 mb-2"
          />
        </InputGroup>
      </RowGroup>
      <RowGroup>
        <InputGroup className="w-100">
          <LabelWrapper>
            <ControlLabel>Complemento:</ControlLabel>
          </LabelWrapper>
          <Field
            type="text"
            name="complement"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (scriptChecker(e?.target.value)) {
                e.target.value = '';
                setFieldValue('complement', '');
                return;
              }
              setFieldValue('complement', e.target.value);
            }}
            data-type="address"
            placeholder="Apartamento, sala, andar, etc."
            className="form-control data-input mb-lg-0 mb-2 "
          />
        </InputGroup>
      </RowGroup>
      <RowGroup>
        <InputGroup className="w-100">
          <LabelWrapper>
            <ControlLabel>Referência:</ControlLabel>
          </LabelWrapper>
          <Field
            type="text"
            name="reference"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (scriptChecker(e?.target.value)) {
                e.target.value = '';
                setFieldValue('reference', '');
                return;
              }
              setFieldValue('reference', e.target.value);
            }}
            data-type="address"
            placeholder="Próximo ao parque"
            className="form-control data-input mb-lg-0 mb-2 "
          />
        </InputGroup>
      </RowGroup>
      <RowGroup>
        <InputGroup className="w-100">
          <LabelWrapper>
            <ControlLabel required>Bairro:</ControlLabel>
          </LabelWrapper>
          <Field
            type="text"
            name="neighborhood"
            placeholder="Aldeota"
            required
            data-type="address"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (scriptChecker(e?.target.value)) {
                e.target.value = '';
                setFieldValue('neighborhood', '');
                return;
              }
              setFieldValue('neighborhood', e.target.value);
            }}
            className="form-control data-input mb-lg-0 mb-2"
            onKeyPress={handleNeighborhoodKeyPress}
          />
        </InputGroup>
      </RowGroup>
      <RowGroup>
        <InputGroup style={{ width: '65%' }}>
          <LabelWrapper>
            <ControlLabel>Cidade:</ControlLabel>
          </LabelWrapper>
          <Field
            type="text"
            name="city"
            disabled
            data-type="address"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (scriptChecker(e?.target.value)) {
                e.target.value = '';
                setFieldValue('city', '');
                return;
              }
              setFieldValue('city', e.target.value);
            }}
            className="form-control data-input mb-lg-0 mb-2"
          />
        </InputGroup>
        <InputGroup style={{ width: '35%' }}>
          <LabelWrapper>
            <ControlLabel>Estado: </ControlLabel>
          </LabelWrapper>
          <Field
            type="text"
            name="state"
            data-type="address"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (scriptChecker(e?.target.value)) {
                e.target.value = '';
                setFieldValue('state', '');
                return;
              }
              setFieldValue('state', e.target.value);
            }}
            disabled
            className="form-control data-input mb-lg-0 mb-2"
          />
        </InputGroup>
      </RowGroup>
      <Button variant="success" type="submit" className="mt-2 w-100 btn btn-secondary px-2">
        <strong style={{ color: 'white' }}>Confirmar endereço</strong>
      </Button>
    </div>
  );
}
